import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { submitEnquiry } from "../lib/api";
import { SITE } from "../lib/site";
import { Reveal } from "./motion";

const schema = z.object({
  name: z.string().min(2, "Please enter your full name."),
  email: z.string().email("Please enter a valid email address."),
  phone: z
    .string()
    .refine(
      (v) => /^(\+91)?[6-9]\d{9}$/.test(v.replace(/[\s\-()]/g, "")),
      "Please enter a valid Indian phone number."
    ),
  service_category: z.string().min(1, "Please choose a service category."),
  message: z
    .string()
    .min(10, "Please describe your enquiry in at least 10 characters.")
    .max(4000),
  consent: z
    .boolean()
    .refine((v) => v === true, "Please confirm you consent to being contacted."),
  company_website: z.string().optional(), // honeypot — must stay empty
});

export const EnquiryForm = ({ compact = false }) => {
  const [reference, setReference] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      service_category: "",
      message: "",
      consent: false,
      company_website: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      const res = await submitEnquiry(values);
      if (res.reference) {
        setReference(res.reference);
        toast.success("Enquiry received", {
          description: `Your reference is ${res.reference}.`,
        });
      } else {
        reset();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    }
  };

  return (
    <div className="relative" data-testid="enquiry-form">
      <AnimatePresence mode="wait">
        {reference ? (
          <motion.div
            key="success"
            data-testid="enquiry-form-success-banner"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-line border-gold/50 bg-cream p-10 text-center"
          >
            <CheckCircle2 className="mx-auto text-gold-dark" size={44} strokeWidth={1.5} />
            <h3 className="mt-5 font-serif text-3xl text-ink">Enquiry received</h3>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-steel">
              Thank you for reaching out. Your reference number is{" "}
              <span className="font-mono font-semibold text-gold-dark">{reference}</span>.
              We will respond to you at the contact details provided. Please do not
              share confidential case documents through this form.
            </p>
            <button
              data-testid="enquiry-form-reset-button"
              onClick={() => {
                setReference(null);
                reset();
              }}
              className="btn-outline mt-8 !px-6 !py-3"
            >
              Submit another enquiry
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            exit={{ opacity: 0, y: -12 }}
            className={compact ? "space-y-5" : "space-y-6"}
          >
            {/* Honeypot — hidden from humans, catnip for bots */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label htmlFor="company_website">Company website</label>
              <input
                id="company_website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                {...register("company_website")}
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="ef-name" className="field-label">
                  Full name *
                </label>
                <input
                  id="ef-name"
                  data-testid="enquiry-form-name-input"
                  className="field-input"
                  placeholder="Your name"
                  aria-invalid={!!errors.name}
                  {...register("name")}
                />
                {errors.name && (
                  <p role="alert" className="mt-2 text-xs text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="ef-email" className="field-label">
                  Email *
                </label>
                <input
                  id="ef-email"
                  data-testid="enquiry-form-email-input"
                  type="email"
                  className="field-input"
                  placeholder="you@company.in"
                  aria-invalid={!!errors.email}
                  {...register("email")}
                />
                {errors.email && (
                  <p role="alert" className="mt-2 text-xs text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="ef-phone" className="field-label">
                  Phone *
                </label>
                <input
                  id="ef-phone"
                  data-testid="enquiry-form-phone-input"
                  type="tel"
                  className="field-input"
                  placeholder="+91 98XXX XXXXX"
                  aria-invalid={!!errors.phone}
                  {...register("phone")}
                />
                {errors.phone && (
                  <p role="alert" className="mt-2 text-xs text-destructive">
                    {errors.phone.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="ef-category" className="field-label">
                  Service category *
                </label>
                <select
                  id="ef-category"
                  data-testid="enquiry-form-service-select"
                  className="field-input appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23525E75%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[position:right_1rem_center] bg-no-repeat pr-10"
                  aria-invalid={!!errors.service_category}
                  {...register("service_category")}
                >
                  <option value="">Select a category…</option>
                  {SITE.serviceCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {errors.service_category && (
                  <p role="alert" className="mt-2 text-xs text-destructive">
                    {errors.service_category.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="ef-message" className="field-label">
                Your enquiry *
              </label>
              <textarea
                id="ef-message"
                data-testid="enquiry-form-message-textarea"
                rows={5}
                className="field-input resize-none"
                placeholder="Briefly describe your legal need. Do not include confidential documents or sensitive personal information."
                aria-invalid={!!errors.message}
                {...register("message")}
              />
              {errors.message && (
                <p role="alert" className="mt-2 text-xs text-destructive">
                  {errors.message.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="ef-consent"
                className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-steel"
              >
                <input
                  id="ef-consent"
                  data-testid="enquiry-form-consent-checkbox"
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[#C5A869]"
                  {...register("consent")}
                />
                <span>
                  I consent to Upgrade Law contacting me about this enquiry and
                  have read the{" "}
                  <a href="/privacy-policy" className="gold-underline font-medium text-ink">
                    Privacy Policy
                  </a>
                  . I understand this form is not for confidential documents. *
                </span>
              </label>
              {errors.consent && (
                <p role="alert" className="mt-2 text-xs text-destructive">
                  {errors.consent.message}
                </p>
              )}
            </div>

            <div className="flex flex-col items-start justify-between gap-4 pt-2 sm:flex-row sm:items-center">
              <button
                type="submit"
                data-testid="enquiry-form-submit-button"
                disabled={isSubmitting}
                className="btn-gold min-w-[220px] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Submitting…
                  </>
                ) : (
                  "Submit a Legal Enquiry"
                )}
              </button>
              <p className="flex items-center gap-2 text-xs text-steel/70">
                <ShieldCheck size={14} className="text-gold-dark" />
                Handled securely. Never send confidential documents.
              </p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
